// ============================================================================
// i18n scaffold — react-i18next, English now + a zh (Traditional) stub.
// ----------------------------------------------------------------------------
// Locale codes match the backend (viccofitness/i18n.py LOCALES = en, zh) so the
// trainer's stored `locale` maps straight through. zh is a STUB: only a starter
// set of keys is translated; everything else falls back to English via
// `fallbackLng`. The active language is persisted to localStorage and (when the
// trainer's profile is known) synced from their account locale by the app.
// Full zh content is a later pass.
// ============================================================================
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enCommon from "./locales/en/common.json";
import zhCommon from "./locales/zh/common.json";
// Per-page namespaces — each page owns its own file so parallel agents never
// collide on common.json. Empty for now; the owning agent fills its namespace.
import enDashboard from "./locales/en/dashboard.json";
import zhDashboard from "./locales/zh/dashboard.json";
import enClientDetail from "./locales/en/clientDetail.json";
import zhClientDetail from "./locales/zh/clientDetail.json";
import enClientReport from "./locales/en/clientReport.json";
import zhClientReport from "./locales/zh/clientReport.json";
import enLanding from "./locales/en/landing.json";
import zhLanding from "./locales/zh/landing.json";
import enClients from "./locales/en/clients.json";
import zhClients from "./locales/zh/clients.json";
import enSessions from "./locales/en/sessions.json";
import zhSessions from "./locales/zh/sessions.json";
import enExercises from "./locales/en/exercises.json";
import zhExercises from "./locales/zh/exercises.json";
import enTemplates from "./locales/en/templates.json";
import zhTemplates from "./locales/zh/templates.json";
import enBusinessReport from "./locales/en/businessReport.json";
import zhBusinessReport from "./locales/zh/businessReport.json";
import enAccount from "./locales/en/account.json";
import zhAccount from "./locales/zh/account.json";
import enLogin from "./locales/en/login.json";
import zhLogin from "./locales/zh/login.json";
import enForgot from "./locales/en/forgot.json";
import zhForgot from "./locales/zh/forgot.json";
import enSignup from "./locales/en/signup.json";
import zhSignup from "./locales/zh/signup.json";
import enAppShell from "./locales/en/appShell.json";
import zhAppShell from "./locales/zh/appShell.json";
import enAttendance from "./locales/en/attendance.json";
import zhAttendance from "./locales/zh/attendance.json";
import enCheckin from "./locales/en/checkin.json";
import zhCheckin from "./locales/zh/checkin.json";
import enActivity from "./locales/en/activity.json";
import zhActivity from "./locales/zh/activity.json";
import enNutrition from "./locales/en/nutrition.json";
import zhNutrition from "./locales/zh/nutrition.json";
import enWeeklyGoals from "./locales/en/weeklyGoals.json";
import zhWeeklyGoals from "./locales/zh/weeklyGoals.json";
import enMessages from "./locales/en/messages.json";
import zhMessages from "./locales/zh/messages.json";
import enInactivity from "./locales/en/inactivity.json";
import zhInactivity from "./locales/zh/inactivity.json";
import enTrainerProfile from "./locales/en/trainerProfile.json";
import zhTrainerProfile from "./locales/zh/trainerProfile.json";
import enPackages from "./locales/en/packages.json";
import zhPackages from "./locales/zh/packages.json";

export const SUPPORTED_LOCALES = ["en", "zh"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  zh: "繁體中文",
};

const STORAGE_KEY = "vf.locale";

function initialLocale(): Locale {
  if (typeof window !== "undefined") {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && (SUPPORTED_LOCALES as readonly string[]).includes(saved)) {
      return saved as Locale;
    }
  }
  return "en";
}

void i18n.use(initReactI18next).init({
  resources: {
    en: {
      common: enCommon,
      dashboard: enDashboard,
      clientDetail: enClientDetail,
      clientReport: enClientReport,
      landing: enLanding,
      clients: enClients,
      sessions: enSessions,
      exercises: enExercises,
      templates: enTemplates,
      businessReport: enBusinessReport,
      account: enAccount,
      login: enLogin,
      forgot: enForgot,
      signup: enSignup,
      appShell: enAppShell,
      attendance: enAttendance,
      checkin: enCheckin,
      activity: enActivity,
      nutrition: enNutrition,
      weeklyGoals: enWeeklyGoals,
      messages: enMessages,
      inactivity: enInactivity,
      trainerProfile: enTrainerProfile,
      packages: enPackages,
    },
    zh: {
      common: zhCommon,
      dashboard: zhDashboard,
      clientDetail: zhClientDetail,
      clientReport: zhClientReport,
      landing: zhLanding,
      clients: zhClients,
      sessions: zhSessions,
      exercises: zhExercises,
      templates: zhTemplates,
      businessReport: zhBusinessReport,
      account: zhAccount,
      login: zhLogin,
      forgot: zhForgot,
      signup: zhSignup,
      appShell: zhAppShell,
      attendance: zhAttendance,
      checkin: zhCheckin,
      activity: zhActivity,
      nutrition: zhNutrition,
      weeklyGoals: zhWeeklyGoals,
      messages: zhMessages,
      inactivity: zhInactivity,
      trainerProfile: zhTrainerProfile,
      packages: zhPackages,
    },
  },
  lng: initialLocale(),
  fallbackLng: "en",
  // Keep `common` as the default NS (existing pages use useTranslation() with no
  // arg). Each per-page agent opts into its own NS via useTranslation("<ns>").
  defaultNS: "common",
  ns: [
    "common",
    "dashboard",
    "clientDetail",
    "clientReport",
    "landing",
    "clients",
    "sessions",
    "exercises",
    "templates",
    "businessReport",
    "account",
    "login",
    "forgot",
    "signup",
    "appShell",
    "attendance",
    "checkin",
    "activity",
    "nutrition",
    "weeklyGoals",
    "messages",
    "inactivity",
    "trainerProfile",
    "packages",
  ],
  // Per-page keys that are missing fall back to the `common` namespace, then to
  // English — so a page can still read shared keys (app.name, actions.*) while
  // its own namespace fills in.
  fallbackNS: "common",
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
});

/**
 * Map the active i18n language to a BCP-47 tag suitable for Intl date/number
 * formatting. `zh` → `zh-Hant` (Traditional Chinese), so month/weekday names and
 * digit grouping localize correctly; everything else passes through. Defaults to
 * the live i18n language so date formatting always follows the chosen UI locale
 * (not the device/browser locale), which is what the timezone convention
 * requires (a HK trainer on 繁中 must see localized month names).
 */
export function intlLocale(locale?: string): string {
  const lng = (locale ?? i18n.language ?? "en").toLowerCase();
  if (lng === "zh" || lng.startsWith("zh-")) return "zh-Hant";
  return lng || "en";
}

/** Change the active language and persist it. */
export function setLocale(locale: Locale): void {
  void i18n.changeLanguage(locale);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, locale);
    document.documentElement.lang = locale === "zh" ? "zh-Hant" : "en";
  }
}

export default i18n;
